import React from 'react'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

/**
 * Enhanced DatePicker component with better UX
 * 
 * @param {Object} props
 * @param {string} props.label - Label for the date input
 * @param {string} props.value - Current date value (YYYY-MM-DD format)
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.minDate - Minimum selectable date (YYYY-MM-DD format)
 * @param {string} props.maxDate - Maximum selectable date (YYYY-MM-DD format)
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.helperText - Helper text below the input
 * @param {boolean} props.error - Whether to show error state
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.fullWidth - Whether to take full width
 * @param {Object} props.sx - Additional MUI sx styling
 * @param {Object} props.inputProps - Additional input props
 * @param {Object} props.otherProps - Other TextField props (spread)
 */
const DatePicker = React.forwardRef(({
	label,
	value,
	onChange,
	minDate,
	maxDate,
	required = false,
	placeholder,
	helperText,
	error = false,
	disabled = false,
	fullWidth = true,
	fontSize = '14px',
	sx = {},
	inputProps = {},
	...otherProps
}, ref) => {
	// Convert string date to dayjs object
	// Only parse if we have a valid date string (YYYY-MM-DD format)
	const dayjsValue = React.useMemo(() => {
		if (!value) return null

		// Check if it's a valid date format (at least YYYY-MM-DD)
		const dateRegex = /^\d{4}-\d{2}-\d{2}$/
		if (!dateRegex.test(value)) return null

		const parsed = dayjs(value)
		return parsed.isValid() ? parsed : null
	}, [value])

	const handleChange = (newValue) => {
		// Convert dayjs object back to string format for onChange
		if (onChange) {
			// Only convert to string if we have a valid dayjs object
			let stringValue = ''
			if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
				stringValue = newValue.format('YYYY-MM-DD')
			}

			const syntheticEvent = {
				target: {
					value: stringValue
				}
			}
			onChange(syntheticEvent)
		}
	}

	// Merge custom styling with user-provided sx
	const mergedSx = {
		'& .MuiInputBase-root': {
			padding: 0,
			fontSize: '14px',
			fontFamily: 'Inter, sans-serif',
			fontWeight: 400,
			lineHeight: '1.21em',
			letterSpacing: '-1.786%'
		},
		'& .MuiPickersSectionList-sectionContent': {
			fontSize: fontSize,
		},
		'& .MuiInputBase-input': {
			padding: 0,
			minHeight: 0,
			height: 'auto'
		},
		'& .MuiInputAdornment-root': {
			marginLeft: 0,
			marginRight: 0
		},
		'& .MuiOutlinedInput-notchedOutline': {
			border: 'none'
		},
		'& fieldset': {
			border: 'none'
		},
		...sx
	}

	// Parse min/max dates safely
	const minDateObj = React.useMemo(() => {
		if (!minDate) return undefined
		const parsed = dayjs(minDate)
		return parsed.isValid() ? parsed : undefined
	}, [minDate])

	const maxDateObj = React.useMemo(() => {
		if (!maxDate) return undefined
		const parsed = dayjs(maxDate)
		return parsed.isValid() ? parsed : undefined
	}, [maxDate])

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<MuiDatePicker
				label={label}
				value={dayjsValue}
				onChange={handleChange}
				minDate={minDateObj}
				maxDate={maxDateObj}
				disabled={disabled}
				format="MM/DD/YYYY"
				slotProps={{
					textField: {
						variant: 'standard',
						fullWidth,
						required,
						placeholder: placeholder || 'MM/DD/YYYY',
						helperText,
						error,
						inputRef: ref,
						sx: mergedSx,
						InputProps: {
							disableUnderline: true
						},
						...otherProps
					}
				}}
			/>
		</LocalizationProvider>
	)
})

DatePicker.displayName = 'DatePicker'

export default DatePicker
